import React, { useCallback, useState } from 'react';
import {
    Box,
    Group,
    ActionIcon,
    NumberInput,
    Select,
    Text,
    Tooltip,
    Badge,
    UnstyledButton,
} from '@mantine/core';
import {
    IconAlertTriangle,
    IconCheck,
    IconMinus,
    IconRotateClockwise2,
} from '@tabler/icons-react';
import clsx from 'clsx';
import { When } from '../common';
import {
    SurveyItemRow,
    DivisionalManagerItemState,
} from '../../pages/price-surveys/types';
import { NA_REASONS } from '../../pages/price-surveys/constants';
import formatCurrency from '../../utility/formatCurrency';
import {
    allCompetitorPricesAreNA,
    PROPOSED_SELLING_BELOW_COST_TOOLTIP,
} from '../../utility/surveyUtils';

const DIVISIONAL_MANAGER_SURVEY_CELL_ATTENTION_CLASSES =
    '!border-2 !border-red-600 !bg-red-100 !shadow-[0_0_0_2px_rgba(220,38,38,0.5)] p-1.5';

const DIVISIONAL_MANAGER_SURVEY_CELL_BELOW_COST_CLASSES =
    '!border-2 !border-[#FFE69C] !bg-[#FFF3CD] !shadow-[0_0_0_2px_rgba(251,191,36,0.35)] p-1.5';

interface ProposedSellingCellProps {
    row: SurveyItemRow;
    itemState: DivisionalManagerItemState | undefined;
    onMarkForPcr: (itemId: string, proposedPrice?: number) => void;
    onMarkAsNA: (itemId: string, reason: string | null) => void;
    onSetProposedPrice: (itemId: string, price: number | null) => void;
    onSetNaReason: (itemId: string, reason: string | null) => void;
    onClearState: (itemId: string) => void;
    disabled?: boolean;
    hasRedSubmitError?: boolean;
    submitErrorMessage?: string;
    belowCostWarningActive?: boolean;
}

export function ProposedSellingCell({
    row,
    itemState,
    onMarkForPcr,
    onMarkAsNA,
    onSetProposedPrice,
    onSetNaReason,
    onClearState,
    disabled = false,
    hasRedSubmitError = false,
    submitErrorMessage,
    belowCostWarningActive = false,
}: ProposedSellingCellProps) {
    const hasPcr = !!row.priceChangeRequestId;
    const noCompetitorPrices = allCompetitorPricesAreNA(row);

    const isApprovedForPcr = itemState?.approvedForPriceChange ?? false;
    const isDeclined = itemState?.isDeclinedForPriceChange ?? false;
    const proposedPrice = itemState?.proposedSellingPrice;
    const declinedReason = itemState?.declinedReason ?? null;

    const handlePcrClick = useCallback(() => {
        if (disabled || hasPcr || noCompetitorPrices) return;

        if (isApprovedForPcr) {
            onClearState(row.id);
        } else {
            const priceToUse =
                proposedPrice ??
                row.suggestedPrice ??
                row.proposedSellingPrice ??
                undefined;
            onMarkForPcr(row.id, priceToUse ?? undefined);
        }
    }, [
        disabled,
        hasPcr,
        noCompetitorPrices,
        isApprovedForPcr,
        proposedPrice,
        row.id,
        row.suggestedPrice,
        row.proposedSellingPrice,
        onMarkForPcr,
        onClearState,
    ]);

    const handleNaClick = useCallback(() => {
        if (disabled || hasPcr || noCompetitorPrices) return;

        if (isDeclined) {
            onClearState(row.id);
        } else {
            onMarkAsNA(row.id, null);
        }
    }, [
        disabled,
        hasPcr,
        noCompetitorPrices,
        isDeclined,
        row.id,
        onMarkAsNA,
        onClearState,
    ]);

    const handleReasonChange = useCallback(
        (value: string | null) => {
            if (disabled) return;
            onSetNaReason(row.id, value);
        },
        [disabled, row.id, onSetNaReason],
    );

    const suggestedPrice = row.suggestedPrice ?? null;
    const hasSuggestedPrice = suggestedPrice != null;
    const showRedo = hasSuggestedPrice && proposedPrice !== suggestedPrice;

    const suggested = row.suggestedPrice ?? '';
    const displayPrice =
        proposedPrice === undefined ? suggested : proposedPrice ?? '';
    const [isEditing, setIsEditing] = useState(false);
    const [editingValue, setEditingValue] = useState<string | number>('');
    const inputDisplayValue = isEditing ? editingValue : displayPrice;

    const commitProposedPrice = useCallback(
        (value: string | number) => {
            if (disabled) return;

            if (value === '' || value == null) {
                onSetProposedPrice(row.id, null);
                return;
            }

            const numValue =
                typeof value === 'string' ? parseFloat(value) : value;
            onSetProposedPrice(
                row.id,
                Number.isNaN(numValue) ? null : numValue,
            );
        },
        [disabled, row.id, onSetProposedPrice],
    );

    const handleResetToSuggested = useCallback(() => {
        if (disabled || row.suggestedPrice == null) return;
        setEditingValue(row.suggestedPrice);
        onSetProposedPrice(row.id, row.suggestedPrice);
    }, [disabled, row.id, row.suggestedPrice, onSetProposedPrice]);

    const handlePriceChange = useCallback(
        (value: string | number) => {
            if (disabled) return;
            setEditingValue(value);
        },
        [disabled],
    );

    const handleInputFocus = useCallback(() => {
        setIsEditing(true);
        setEditingValue(displayPrice);
    }, [displayPrice]);

    const handleInputBlur = useCallback(() => {
        setIsEditing(false);

        let nextPrice: number | null = null;
        if (editingValue !== '' && editingValue != null) {
            nextPrice =
                typeof editingValue === 'string'
                    ? parseFloat(editingValue)
                    : editingValue;
        }

        const normalizedNext =
            nextPrice == null || Number.isNaN(nextPrice) ? null : nextPrice;

        const currentPrice =
            proposedPrice === undefined
                ? suggestedPrice ?? null
                : proposedPrice ?? null;

        if (normalizedNext === currentPrice) {
            return;
        }

        commitProposedPrice(editingValue);
    }, [commitProposedPrice, editingValue, proposedPrice, suggestedPrice]);

    const handleInputKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Enter') return;
            event.currentTarget.blur();
        },
        [],
    );

    const currency = row.currency ?? 'ZAR';
    const placeholderPrice = row.suggestedPrice
        ? formatCurrency(row.suggestedPrice, currency)
        : formatCurrency(0, currency);
    const inputEmpty =
        !isDeclined && (displayPrice === '' || displayPrice === 0);
    const showProposedSellingControls = !hasPcr && !noCompetitorPrices;

    const needsRedAttentionHighlight =
        hasRedSubmitError || (showProposedSellingControls && inputEmpty);

    const needsBelowCostHighlight =
        belowCostWarningActive &&
        showProposedSellingControls &&
        !needsRedAttentionHighlight;

    const declinedButtonHighlight = isDeclined;
    const pcrButtonHighlight = isApprovedForPcr;

    const cellContent = (
        <Box
            className={clsx(
                'w-full min-h-[2rem] rounded',
                needsRedAttentionHighlight &&
                    DIVISIONAL_MANAGER_SURVEY_CELL_ATTENTION_CLASSES,
                needsBelowCostHighlight &&
                    DIVISIONAL_MANAGER_SURVEY_CELL_BELOW_COST_CLASSES,
            )}
        >
            <When condition={hasPcr}>
                <Group gap="xs" wrap="nowrap" className="w-full">
                    <Badge color="green" variant="light" size="sm">
                        PCR Created
                    </Badge>
                    <Text size="sm" fw={500} c="green.7">
                        {formatCurrency(
                            row.priceChangeRequest?.proposedPrice ?? 0,
                            currency,
                        )}
                    </Text>
                </Group>
            </When>

            <When condition={!hasPcr && noCompetitorPrices}>
                <Text size="sm" c="dimmed" className="italic">
                    No competitor prices
                </Text>
            </When>

            <When condition={showProposedSellingControls}>
                <Group gap="xs" wrap="nowrap" className="w-full">
                    <span className="shrink-0 flex w-[18px] items-center justify-center">
                        <Tooltip
                            label={PROPOSED_SELLING_BELOW_COST_TOOLTIP}
                            multiline
                            maw={280}
                            withArrow
                            disabled={!needsBelowCostHighlight}
                        >
                            <span
                                className={clsx(
                                    'flex items-center text-[#856404]',
                                    !needsBelowCostHighlight && 'invisible',
                                )}
                                aria-hidden={!needsBelowCostHighlight}
                            >
                                <IconAlertTriangle size={18} stroke={2} />
                            </span>
                        </Tooltip>
                    </span>
                    <When
                        condition={isDeclined}
                        fallback={
                            <NumberInput
                                size="xs"
                                value={inputDisplayValue}
                                onChange={handlePriceChange}
                                onFocus={handleInputFocus}
                                onBlur={handleInputBlur}
                                onKeyDown={handleInputKeyDown}
                                min={0.01}
                                decimalScale={2}
                                thousandSeparator=""
                                prefix={`${(currency ?? 'ZAR')
                                    .trim()
                                    .toUpperCase()} `}
                                placeholder={placeholderPrice}
                                hideControls
                                disabled={disabled}
                                allowDecimal
                                rightSection={
                                    showRedo ? (
                                        <Tooltip label="Reset to suggested price">
                                            <UnstyledButton
                                                onClick={handleResetToSuggested}
                                                onMouseDown={e =>
                                                    e.preventDefault()
                                                }
                                                className="p-0 cursor-pointer flex items-center justify-center"
                                                aria-label="Reset to suggested price"
                                            >
                                                <IconRotateClockwise2
                                                    size={14}
                                                    className="text-primary"
                                                />
                                            </UnstyledButton>
                                        </Tooltip>
                                    ) : undefined
                                }
                                rightSectionPointerEvents={
                                    showRedo ? 'auto' : undefined
                                }
                                rightSectionWidth={showRedo ? 28 : undefined}
                                classNames={{
                                    input: 'h-8 text-sm rounded-xl border-gray-300',
                                    root: 'flex-1',
                                    controls: 'hidden',
                                    section: showRedo
                                        ? '!pr-2 flex items-center justify-end'
                                        : undefined,
                                }}
                            />
                        }
                    >
                        <Select
                            size="sm"
                            value={declinedReason}
                            onChange={handleReasonChange}
                            placeholder="Select reason"
                            data={NA_REASONS.map(r => ({
                                value: r,
                                label: r,
                            }))}
                            disabled={disabled}
                            clearable
                            classNames={{
                                input: 'h-8 text-sm rounded-xl flex-1 min-h-0 border-gray-300',
                                root: 'flex-1',
                                option: 'text-sm hover:bg-primary/10',
                                dropdown: 'text-sm',
                            }}
                        />
                    </When>

                    <Tooltip
                        label={
                            isDeclined ? 'Remove declined' : 'Mark as declined'
                        }
                    >
                        <span>
                            <ActionIcon
                                size="sm"
                                variant={
                                    declinedButtonHighlight
                                        ? 'filled'
                                        : 'outline'
                                }
                                color={
                                    declinedButtonHighlight
                                        ? 'progressRed'
                                        : 'gray'
                                }
                                onClick={handleNaClick}
                                disabled={disabled}
                            >
                                <IconMinus size={14} />
                            </ActionIcon>
                        </span>
                    </Tooltip>

                    <Tooltip
                        label={
                            isApprovedForPcr
                                ? 'Remove approval for PCR'
                                : 'Approve for PCR'
                        }
                    >
                        <span>
                            <ActionIcon
                                size="sm"
                                variant={
                                    pcrButtonHighlight ? 'filled' : 'outline'
                                }
                                color={pcrButtonHighlight ? 'blue' : 'gray'}
                                onClick={handlePcrClick}
                                disabled={
                                    disabled ||
                                    (!isDeclined &&
                                        (Number(displayPrice) || 0) <= 0)
                                }
                            >
                                <IconCheck size={14} />
                            </ActionIcon>
                        </span>
                    </Tooltip>
                </Group>
            </When>
        </Box>
    );

    return (
        <Tooltip
            label={submitErrorMessage ?? ''}
            multiline
            maw={320}
            disabled={!(hasRedSubmitError && submitErrorMessage)}
        >
            {cellContent}
        </Tooltip>
    );
}
